import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Row, Col } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { Button, CardHeader, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { customFilterOption } from "../../../../../utility/Utility";
import { useLazyGetMenuItemAllListQuery } from "../../../../../redux/services/setMenuService";
import { DEFAULT_IMAGE_URL } from "../../../../../constants/constans";
import BreadCrumbs from "../../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../../components/ControlledRowTextField";
import ButtonComponent from "../../../../../components/ButtonComponent";
import { useCreateProductionPlanMutation } from "../../../../../redux/services/DistributionService";

export interface ProductionPlanItem {
  itemId: string;
  displayName: string;
  plannedQty: number;
  productId: string;
  itemType: string;
  category: string;
  portionSize: string;
  unitPrice: number;
  abbreviation?: string;
  classFilterTag?: string;
  skuId?: string;
}

export interface ProductionPlanFormData {
  restaurantId?: string;
  displayName: string;
  items: ProductionPlanItem[];
}

const AddProductionPlan = () => {
  const navigate = useNavigate();
  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Initial form values
  const defaultValues: ProductionPlanFormData = {
    displayName: "",
    items: [],
  };

  // Form handling
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ProductionPlanFormData>({
    defaultValues,
  });

  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<ProductionPlanItem[]>([]);
  const [plannedQty, setPlannedQty] = useState<number>(0);
  const [selectedPortion, setSelectedPortion] = useState<any>(null);
  const [availableOptions, setAvailableOptions] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isActive, setIsActive] = useState(false);

  const [triggerGetMenuItemList, { isLoading, data: menuItemsData }] =
    useLazyGetMenuItemAllListQuery();
  const [createProductionPlanMutation] = useCreateProductionPlanMutation();

  useEffect(() => {
    if (RestID) {
      triggerGetMenuItemList({ restaurantId: RestID });
    }
  }, [RestID, triggerGetMenuItemList]);

  useEffect(() => {
    if (menuItemsData) {
      const options = menuItemsData.map((item: any) => ({
        value: item.itemId,
        displayName: item.displayName,
        abbreviation: item.abbreviation || "",
        label: (
          <div style={{ display: "flex", justifyContent: "left", gap: "5%" }}>
            <img
              src={item.imageUrl || DEFAULT_IMAGE_URL}
              alt=""
              width="30px"
              height="30px"
            />
            {item.productId} | {item.displayName}
          </div>
        ),
        description: item.description,
        price: item.price,
        imageUrl: item.imageUrl,
        productId: item.productId,
        itemType: item.itemType,
        category: item.category,
        portions: item.portions || [
          { displayName: "REGULAR", additionalCost: 0 },
        ],
      }));
      setAvailableOptions(options);
    }
  }, [menuItemsData]);

  // Handle production plan creation
  const createProductionPlan = async (data: ProductionPlanFormData) => {
    try {
      setIsSaving(true);

      // Prepare the payload with the correct field names
      const payload: ProductionPlanFormData = {
        displayName: data.displayName,
        restaurantId: RestID,
        items: selectedItems.map((item) => ({
          itemId: item.itemId,
          displayName: item.displayName,
          plannedQty: item.plannedQty,
          productId: item.productId,
          itemType: item.itemType,
          category: item.category,
          portionSize: item.portionSize,
          unitPrice: item.unitPrice,
          abbreviation: item.abbreviation || "",
        })),
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await createProductionPlanMutation(payload).unwrap();

      toast.success("Production Plan created successfully");
      navigate("/distribution/orderManagement");
    } catch (error: any) {
      if (error.status === 409) {
        toast.error(
          "This production plan name already exists. Please use a different name."
        );
      } else {
        toast.error(error?.data?.message || "Failed to create production plan");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    if (selectedOption && selectedPortion) {
      const selectedPortionDetails = selectedOption.portions.find(
        (portion: any) => portion.displayName === selectedPortion.value
      );

      const additionalCost = selectedPortionDetails?.additionalCost || 0;
      const basePrice = selectedOption.price || 0;
      const totalPrice = basePrice + additionalCost;

      const newItem: ProductionPlanItem = {
        itemId: selectedOption.value,
        displayName: selectedOption.displayName,
        plannedQty: plannedQty,
        productId: selectedOption.productId,
        itemType: selectedOption.itemType,
        category: selectedOption.category,
        portionSize: selectedPortion.value,
        unitPrice: totalPrice,
        abbreviation: selectedOption.abbreviation || "",
      };
      setSelectedItems([...selectedItems, newItem]);
      setSelectedOption(null);
      setSelectedPortion(null);
      setPlannedQty(0);
      setErrorMessage(null);
    } else {
      setErrorMessage("Please select an item and portion to add.");
    }
  };

  const handleDelete = (index: number) => {
    const updatedItems = [...selectedItems];
    updatedItems.splice(index, 1);
    setSelectedItems(updatedItems);
  };

  // Get portion options for the selected item, filtering out already selected portions
  const getPortionOptions = () => {
    if (!selectedOption) return [];

    // Get all portions for the selected item
    const portions = selectedOption.portions || [
      { displayName: "REGULAR", additionalCost: 0 },
    ];

    // Find which portions are already selected for this item
    const selectedPortionsForItem = selectedItems
      .filter((item) => item.itemId === selectedOption.value)
      .map((item) => item.portionSize);

    // Filter out already selected portions
    const availablePortions = portions.filter(
      (portion: any) => !selectedPortionsForItem.includes(portion.displayName)
    );

    return availablePortions.map((portion: any) => ({
      value: portion.displayName,
      label: portion.displayName,
    }));
  };

 const calculateTotalQuantity = () => {
  return selectedItems.reduce((total, item) => total + item.plannedQty, 0);
};
  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <br />
      <Card>
        <CardHeader style={{ backgroundColor: "#fff" }}>
          <h4>Create Production Plan</h4>
        </CardHeader>
        <CardBody>
          <form
            className="auth-login-form mt-3"
            onSubmit={handleSubmit(createProductionPlan)}
          >
            <ControlledRowTextField
              label={"Production Plan Name"}
              inputName={"displayName"}
              inputType={"text"}
              placeholder={"Enter Production Plan Name"}
              isRequired={true}
              control={control}
              rules={{ required: "Production Plan Name is required" }}
            />
            {errors.displayName && (
              <span className="text-danger">
                {errors.displayName.message as string}
              </span>
            )}

            <hr />
          </form>
        </CardBody>
      </Card>

      <Card className="mt-3">
        <CardBody>
          <Row className="align-items-center mt-3">
            <Col sm={5}>
              <Select
                value={selectedOption}
                onChange={(option) => {
                  setSelectedOption(option);
                  setSelectedPortion(null); // Reset portion selection when item changes
                }}
                options={availableOptions}
                placeholder={isLoading ? "Loading..." : "Search menu item"}
                isSearchable
                isDisabled={!availableOptions}
                loadingMessage={() => "Loading menu items..."}
                noOptionsMessage={() =>
                  isLoading ? "Loading..." : "No items found"
                }
                filterOption={customFilterOption}
              />
            </Col>
            <Col sm={3}>
              <Select
                value={selectedPortion}
                onChange={setSelectedPortion}
                options={getPortionOptions()}
                placeholder="Select portion"
                isSearchable={false}
                isDisabled={!selectedOption || getPortionOptions().length === 0}
              />
              {selectedOption && getPortionOptions().length === 0 && (
                <small
                  className="text-danger"
                  style={{
                    position: "absolute",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  All portions for this item have been added
                </small>
              )}
            </Col>
            <Col sm={2}>
              {/* <input
                type="number"
                className="form-control"
                min="0"
                value={plannedQty}
                onChange={(e) => setPlannedQty(Number(e.target.value))}
                placeholder="Quantity"
              /> */}
              <input
                type="number"
                className="form-control"
                value={plannedQty === 0 ? "" : plannedQty}
                onChange={(e) => {
                  const value = e.target.value;
                  setPlannedQty(value === "" ? 0 : Number(value) || 0);
                }}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = "";
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    setPlannedQty(0);
                  }
                }}
                placeholder="0"
                min="0"
              />
            </Col>
            <Col sm={2} className="text-right">
              <ButtonComponent
                titel="+Add Item"
                type="button"
                onClick={handleAdd}
                isDisabled={
                  !selectedOption || !selectedPortion || plannedQty < 1
                }
              />
            </Col>
          </Row>
          <br />
          <div className="selected-items-container">
            <h5>Selected Items</h5>
            <Table bordered className="mt-3" responsive>
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Name</th>
                  <th>Portion Size</th>
                  <th>Quantity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.productId}</td>
                    <td>{item.displayName}</td>
                    <td>{item.portionSize}</td>
                    <td>{item.plannedQty}</td>
                    <td>
                      <Button
                        variant="light"
                        onClick={() => handleDelete(index)}
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          title="Delete"
                          color="#F24E1E"
                        />
                      </Button>
                    </td>
                  </tr>
                ))}
                {selectedItems.length > 0 && (
      <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
        <td colSpan={3} className="text-end">
          Total Quantity:
        </td>
        <td>{calculateTotalQuantity()}</td>
        <td></td>
      </tr>
    )}
              </tbody>
            </Table>
            {errorMessage && (
              <div className="text-danger mt-2">{errorMessage}</div>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-end m-4">
        {isSaving ? (
          <ButtonComponent
            icon={<FontAwesomeIcon icon={faSpinner} spin />}
            titel="Saving..."
            isDisabled={true}
          />
        ) : (
          <ButtonComponent
            titel="Save Production Plan"
            type="button"
            onClick={handleSubmit(createProductionPlan)}
            isDisabled={selectedItems.length === 0}
          />
        )}
      </div>
    </>
  );
};

export default AddProductionPlan;
